import React from 'react';
import { useEnforceLoginStatus } from '../../../hooks/useEnforceLoginStatus';
import useHideRootNavigationHeader from '../../../hooks/useHideRootNavigationHeader';
import DrawerNavigation from './DrawerNavigation';
import BottomTabNavigation from './BottomTabNavigation';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import EnforceUserRegistration from '../../shared/EnforceUserOnboarding';
import { Authenticator } from '@aws-amplify/ui-react-native';
import withOnlyMountOnFocus from '../../../utils/withOnlyMountOnFocus';
import { useConfigureAnalytics } from '../../../hooks/useConfigureAnalytics';
import { useRecordCapturedUrlUTMParameters } from '../../../hooks/useUrlUTMParameters';
import OrganizationInvitationGate from '../../shared/OrganizationInvitationGate';
import ErrorBoundary from '../../v2BaseComponents/ErrorBoundary';
import ReconcileProConsumerNavigation from '../../shared/ReconcileProConsumerNavigation';
import { useRegisterDevicePushToken } from '../../../hooks/useRegisterDevicePushToken';
import { useRegisterUserPlatform } from '../../../hooks/api/useRegisterUserPlatform';
import { SessionProvider, ActivityTracker, useAutoLogout } from '../../SessionProvider';
import { useOrganizationSessionTimeout } from '../../../hooks/api/useOrganization';

export type TabNavigationOwnProps = {};

export type TabNavigationProps = TabNavigationOwnProps;

/**
 * Navigation entry for a user that has logged in or signed up and has registered (entered required user data).
 */
const TabNavigation: React.FC<TabNavigationProps> = () => {
    // register the device push token for push notifications
    useRegisterDevicePushToken();

    useHideRootNavigationHeader();
    useEnforceLoginStatus({ shouldBeLoggedIn: true });
    // configure analytics now that we have a logged in user
    useConfigureAnalytics();
    // record any UTM parameters captured from the URL
    useRecordCapturedUrlUTMParameters();
    // register platform
    useRegisterUserPlatform();

    const isMobileLayout = useIsMobileLayout();

    const renderAppContent = () => {
        return isMobileLayout ? <BottomTabNavigation /> : <DrawerNavigation />;
    };

    const renderWithReconciledNavigation = () => <ReconcileProConsumerNavigation renderContent={renderAppContent} />;

    const renderWithOrganizationInvitationGate = () => {
        return <OrganizationInvitationGate renderContent={renderWithReconciledNavigation} />;
    };

    return (
        <ErrorBoundary>
            <Authenticator.Provider>
                <SessionProvider>
                    <ActivityTracker>
                        <WithAutoLogout renderWithOrganizationInvitationGate={renderWithOrganizationInvitationGate} />
                    </ActivityTracker>
                </SessionProvider>
            </Authenticator.Provider>
        </ErrorBoundary>
    );
};

// TabNavigation.whyDidYouRender = true;

export default React.memo(withOnlyMountOnFocus(TabNavigation));

export type WithAutoLogoutProps = {
    renderWithOrganizationInvitationGate: () => React.ReactElement;
};

const WithAutoLogout: React.FC<WithAutoLogoutProps> = ({ renderWithOrganizationInvitationGate }) => {
    const sessionTimeout = useOrganizationSessionTimeout();
    const { useSetTimeout } = useAutoLogout();
    useSetTimeout(sessionTimeout);
    return <EnforceUserRegistration renderApp={renderWithOrganizationInvitationGate} />;
};
